import { $, $$ } from '@utils';
import removeAccentMarkAndUppercase from '@utils/RemoveAccentMarkAndUppercase';
import Form from '@elements/Forms/Form/Form';
import shouldBlockEnterOnSelectionControl from '@utils/ShouldBlockEnterOnSelectionControl';

function init() {
  if (!$('[data-au-emergent-form]')) return;
  $$('[data-au-emergent-form]').forEach((wrapper) => {
    markRequiredInputs(wrapper);
    disableHiddenStepInputs(wrapper);
    requiredFieldsGate(wrapper);
    stepNavigation(wrapper);
    keyboardNavigation(wrapper);
    datalayerEmergentForm(wrapper);
    submitDataLayer(wrapper);
  });
}

function markRequiredInputs(wrapper) {
  wrapper.querySelectorAll('input[required]').forEach((input) => {
    input.dataset.auEmergentFormRequired = 'true';
  });
}

function toggleStepInputs(step, isActive) {
  step.querySelectorAll('input').forEach((input) => {
    input.required = isActive && input.dataset.auEmergentFormRequired === 'true';
  });
}

function disableHiddenStepInputs(wrapper) {
  wrapper.querySelectorAll('[data-au-emergent-form-content].au-hide').forEach((step) => {
    toggleStepInputs(step, false);
  });
}

function isFieldValid(input) {
  if (input.type === 'checkbox' || input.type === 'radio') return input.checked;
  return input.value.trim() !== '' && input.checkValidity();
}

function requiredFieldsGate(wrapper) {
  wrapper.querySelectorAll('[data-au-emergent-form-content]').forEach((step) => {
    const advanceButton = step.querySelector('[data-au-emergent-form-button], [data-au-emergent-form-submit]');
    if (!advanceButton) return;

    const requiredInputs = step.querySelectorAll('input[data-au-emergent-form-required="true"]');
    if (!requiredInputs.length) return;

    const updateState = () => {
      advanceButton.disabled = ![...requiredInputs].every(isFieldValid);
    };

    requiredInputs.forEach((input) => {
      input.addEventListener('input', updateState);
      input.addEventListener('change', updateState);
    });

    updateState();
  });
}

function stepNavigation(wrapper) {
  wrapper.querySelectorAll('[data-au-emergent-form-trigger]').forEach((trigger) => {
    trigger.addEventListener('click', (e) => {
      if (trigger.hasAttribute('data-au-emergent-form-button')) {
        e.preventDefault();

        if (!validateCurrentStep(wrapper)) {
          return;
        }
      }

      const hide = wrapper.querySelector(`[data-au-emergent-form-content="${trigger.dataset.auEmergentFormHide}"]`);
      const show = wrapper.querySelector(`[data-au-emergent-form-content="${trigger.dataset.auEmergentFormShow}"]`);

      if (hide) {
        hide.classList.add('au-hide');
        toggleStepInputs(hide, false);
      }

      if (show) {
        show.classList.remove('au-hide');
        toggleStepInputs(show, true);
        const firstInput = show.querySelector('input');
        if (firstInput) {
          firstInput.focus();
        }
      }
    });
  });
}

function validateCurrentStep(wrapper) {
  const currentStep = wrapper.querySelector('[data-au-emergent-form-content]:not(.au-hide)');
  if (!currentStep) return true;

  const validator = Form.getValidator?.();
  if (!validator) return true;

  const invalidFields = validator.validateAll(currentStep);
  if (!invalidFields || !invalidFields.length) return true;

  invalidFields[0].focus();
  return false;
}

function keyboardNavigation(wrapper) {
  const form = wrapper.querySelector('[data-au-emergent-form-form]');
  if (!form) return;

  form.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    if (e.defaultPrevented) return;
    if (e.target.tagName === 'TEXTAREA') return;

    if (shouldBlockEnterOnSelectionControl(e.target)) {
      e.preventDefault();
      return;
    }

    const currentStep = form.querySelector('[data-au-emergent-form-content]:not(.au-hide)');
    if (!currentStep) return;
    if (currentStep.hasAttribute('data-au-emergent-form-last')) return;

    const visiblePredictiveList = currentStep.querySelector('[data-au-listbox]:not(.au-visually-hidden)');
    if (visiblePredictiveList) return;

    const nextButton = currentStep.querySelector('[data-au-emergent-form-button]');
    if (!nextButton) return;

    e.preventDefault();
    nextButton.click();
  });
}

function submitDataLayer(wrapper) {
  const form = wrapper.querySelector('[data-au-emergent-form-form]');
  if (!form) return;

  form.addEventListener('submit', () => {
    const title = wrapper.querySelector('[data-au-title]');
    const titleText = title ? title.innerText : '';
    window.dataLayer.push({
      event: 'generate_lead',
      content_type: 'lead_generation',
      item_id: `lead_generation_${removeAccentMarkAndUppercase(titleText)}`,
    });
  });
}

function getFormTitle(wrapper) {
  return wrapper.querySelector('[data-au-title]')?.innerText.trim() ?? '';
}

function getStepTitle(step) {
  return step?.querySelector('[data-au-progress-info-steps]')?.innerText.trim() ?? '';
}

function getLabelText(element) {
  const label = element.querySelector('span:not([data-au-icon]):not([data-au-hidden-label])');
  return (label ? label.innerText : element.innerText).trim();
}

function pushNavigation(contentType, parts) {
  const item_id = parts.filter(Boolean).map(removeAccentMarkAndUppercase).join('_');

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'navigation',
    content_type: contentType,
    item_id,
  });
}

function datalayerEmergentForm(wrapper) {
  const formTitle = getFormTitle(wrapper);

  wrapper.querySelectorAll('[data-au-emergent-form-trigger]').forEach((trigger) => {
    const currentStep = wrapper.querySelector(
      `[data-au-emergent-form-content="${trigger.dataset.auEmergentFormHide}"]`,
    );
    const isNext = trigger.hasAttribute('data-au-emergent-form-button');
    const contentType = isNext ? 'clickBoton' : 'clickEnlace';

    trigger.addEventListener('click', () => {
      // The next button only advances when validation passes, hiding the current step.
      if (isNext && !currentStep?.classList.contains('au-hide')) return;

      pushNavigation(contentType, [formTitle, getStepTitle(currentStep), getLabelText(trigger)]);
    });
  });

  const closeButton = wrapper.closest('[data-au-modal]')?.querySelector('button[data-micromodal-close]');
  if (closeButton) {
    closeButton.addEventListener('click', () => {
      const visibleStep = wrapper.querySelector('[data-au-emergent-form-content]:not(.au-hide)');
      const closeLabel = closeButton.getAttribute('aria-label') ?? 'cerrar';
      pushNavigation('clickBoton', [formTitle, getStepTitle(visibleStep), closeLabel]);
    });
  }

  wrapper.querySelectorAll('[data-au-emergent-form-state]').forEach((state) => {
    const stateButton = state.querySelector('[data-au-emergent-form-state-btn]');
    if (!stateButton) return;

    const stateTitle = state.querySelector('[data-au-emergent-form-state-title]')?.innerText.trim() ?? '';
    const contentType = stateButton.hasAttribute('data-au-download-file') ? 'clickDescarga' : 'clickEnlace';

    stateButton.addEventListener('click', () => {
      pushNavigation(contentType, [stateTitle, getLabelText(stateButton)]);
    });
  });
}

export default { init };
